export const SHARED_CHECKLIST_SCHEMA_VERSION = 1 as const;

export interface SharedChecklistPayload {
    title: string;
    description: string;
    pinIds: string[];
}

type WireTuple = [typeof SHARED_CHECKLIST_SCHEMA_VERSION, string, string, string[]];

async function deflateRaw(input: Uint8Array): Promise<Uint8Array> {
    const cs = new CompressionStream('deflate-raw');
    const writer = cs.writable.getWriter();
    writer.write(input);
    writer.close();
    return new Uint8Array(await new Response(cs.readable).arrayBuffer());
}

async function inflateRaw(input: Uint8Array): Promise<Uint8Array> {
    const ds = new DecompressionStream('deflate-raw');
    const writer = ds.writable.getWriter();
    writer.write(input);
    writer.close();
    return new Uint8Array(await new Response(ds.readable).arrayBuffer());
}

function bytesToBase64Url(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i] as number);
    return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

function base64UrlToBytes(value: string): Uint8Array {
    const padded = value.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - (value.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

export async function encodeSharedChecklist(payload: SharedChecklistPayload): Promise<string> {
    const tuple: WireTuple = [SHARED_CHECKLIST_SCHEMA_VERSION, payload.title, payload.description, payload.pinIds];
    const json = JSON.stringify(tuple);
    const bytes = new TextEncoder().encode(json);
    const compressed = await deflateRaw(bytes);
    return bytesToBase64Url(compressed);
}

export async function decodeSharedChecklist(encoded: string): Promise<SharedChecklistPayload | null> {
    try {
        const compressed = base64UrlToBytes(encoded);
        const bytes = await inflateRaw(compressed);
        const json = new TextDecoder().decode(bytes);
        const parsed = JSON.parse(json) as unknown;
        if (!Array.isArray(parsed) || parsed.length !== 4) return null;
        const [version, title, description, pinIds] = parsed as WireTuple;
        if (version !== SHARED_CHECKLIST_SCHEMA_VERSION) return null;
        if (typeof title !== 'string' || typeof description !== 'string') return null;
        if (!Array.isArray(pinIds) || !pinIds.every(id => typeof id === 'string')) return null;
        return { title, description, pinIds };
    } catch {
        return null;
    }
}
